/* src/components/AddMatchModal.js */
import React, { useEffect, useState } from "react";
import axios from "axios";
import "./css/GamesStatsPage.css"; // Ensure this path is correct

const API_URL =
  "https://vhlyllb4ea.execute-api.us-east-2.amazonaws.com/prod/matchStatsAPI";

const DEBUG_MODE = true;

/**
 * AddMatchModal:
 *  - isOpen: bool
 *  - onClose: function to close
 *  - userId: string
 *  - teams: array of team objects
 *  - refreshMatches: function to refresh the main list on save
 */

const AddMatchModal = ({ isOpen, onClose, userId, teams, refreshMatches }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  // Track selected team
  const [selectedTeamId, setSelectedTeamId] = useState("");
  const [selectedTeamName, setSelectedTeamName] = useState(""); // New state

  // Match data
  const [matchData, setMatchData] = useState({
    opposition: "",
    homeAway: "Home",
    match_date: "",
    teamScores: { myTeamScore: 0, oppositionScore: 0 },
    playerStats: [], // Will be populated with players
  });

  // Accordion States
  const [isMatchDetailsOpen, setIsMatchDetailsOpen] = useState(false);
  const [isPlayerStatsOpen, setIsPlayerStatsOpen] = useState(false);
  const [expandedPlayers, setExpandedPlayers] = useState({}); // Track expanded state for each player

  // All players in the selected team
  const [players, setPlayers] = useState([]);

  /**
   * On modal open, set the selected team and fetch players and team info
   */
  useEffect(() => {
    if (isOpen) {
      if (teams.length === 1) {
        setSelectedTeamId(teams[0].team_id);
        debugLog("Single team found, selectedTeamId:", teams[0].team_id);
        // Fetch players and team info
        fetchTeamPlayers(teams[0].team_id);
        fetchTeamInfo(teams[0].team_id); // Fetch team name from API
      } else if (teams.length > 1) {
        setSelectedTeamId(""); // Reset selection
        setSelectedTeamName(""); // Reset team name
        setPlayers([]); // Reset players
        setMatchData((prev) => ({ ...prev, playerStats: [] }));
        debugLog("Multiple teams available, awaiting team selection.");
      } else {
        setError("No teams available to add a match.");
        debugLog("No teams available.");
      }
    } else {
      // Reset state when modal is closed
      setSelectedTeamId("");
      setSelectedTeamName(""); // Reset team name
      setPlayers([]);
      setMatchData({
        opposition: "",
        homeAway: "Home",
        match_date: "",
        teamScores: { myTeamScore: 0, oppositionScore: 0 },
        playerStats: [],
      });
      setExpandedPlayers({});
      setError("");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  /**
   * Fetch team information based on team_id
   */
  const fetchTeamInfo = async (teamId) => {
    try {
      const requestBody = {
        operationType: "get_team_info",
        data: {
          team_id: teamId,
        },
      };

      const response = await axios.post(API_URL, requestBody, {
        headers: { "Content-Type": "application/json" },
      });

      if (response.status === 200) {
        const { team_info } = response.data;
        if (team_info && team_info.team_name) {
          setSelectedTeamName(team_info.team_name);
          debugLog("Fetched team name:", team_info.team_name);
        } else {
          setSelectedTeamName("Unknown Team");
          debugLog("Team name not found in response.");
        }
      } else {
        setSelectedTeamName("Unknown Team");
        debugLog("Failed to fetch team info:", response.data.error);
      }
    } catch (err) {
      console.error("Error fetching team info:", err);
      setSelectedTeamName("Unknown Team");
      setError("Unable to fetch team information. Check console.");
    }
  };

  /**
   * Fetch all players for the selected team
   */
  const fetchTeamPlayers = async (teamId) => {
    setLoading(true);
    setError("");
    try {
      // Define an operation to fetch team players, e.g., "get_team_players"
      const requestBody = {
        operationType: "get_team_players",
        data: {
          team_id: teamId,
        },
      };
      console.log(
        "[DEBUG] Fetching team players with requestBody:",
        requestBody
      );

      const response = await axios.post(API_URL, requestBody, {
        headers: { "Content-Type": "application/json" },
      });
      console.log("[DEBUG] Response from get_team_players:", response);

      if (response.status === 200) {
        // Suppose it returns { players: [...] }
        const { players } = response.data;
        if (players && Array.isArray(players)) {
          setPlayers(players);
          // Initialize playerStats with all players
          const initialPlayerStats = players.map((player) => ({
            player_id: player.player_id,
            player_name: player.player_name,
            position: player.position || "",
            played: "played", // Default value
            goals: 0,
            assists: 0,
            shots_on_target: 0,
            shots_off_target: 0,
            defensive_actions: 0,
            saves: 0,
          }));
          setMatchData((prev) => ({
            ...prev,
            playerStats: initialPlayerStats,
          }));
          debugLog("PlayerStats initialized:", initialPlayerStats);
        } else {
          setError("No players found for the selected team.");
        }
      } else {
        setError(
          "Failed to fetch team players. " + (response.data.error || "")
        );
      }
    } catch (err) {
      console.error("Error fetching team players:", err);
      setError("Unable to fetch team players. Check console.");
    } finally {
      setLoading(false);
    }
  };

  /**
   * Handle Team Selection Change
   */
  const handleTeamSelection = (e) => {
    const teamId = e.target.value;
    setSelectedTeamId(teamId);

    if (teamId) {
      fetchTeamPlayers(teamId);
      fetchTeamInfo(teamId); // Fetch team name from API
    } else {
      setSelectedTeamName(""); // Reset if no team is selected
      setPlayers([]);
      setMatchData((prev) => ({ ...prev, playerStats: [] }));
    }
  };

  /**
   * Handle Match-Level Field Changes
   */
  const handleMatchFieldChange = (field, value) => {
    setMatchData((prev) => ({ ...prev, [field]: value }));
  };

  /**
   * Handle Team Score Changes
   */
  const handleTeamScoreChange = (field, value) => {
    const numericValue = parseInt(value, 10);
    setMatchData((prev) => ({
      ...prev,
      teamScores: {
        ...prev.teamScores,
        [field]: isNaN(numericValue) ? 0 : numericValue,
      },
    }));
  };

  /**
   * Handle Player Stat Changes
   */
  const handlePlayerStatChange = (index, field, value) => {
    let updatedValue = value;
    // Parse numerical fields
    if (
      [
        "goals",
        "assists",
        "shots_on_target",
        "shots_off_target",
        "defensive_actions",
        "saves",
      ].includes(field)
    ) {
      const parsed = parseInt(value, 10);
      updatedValue = isNaN(parsed) ? 0 : parsed;
    }
    setMatchData((prev) => {
      const updatedStats = [...prev.playerStats];
      updatedStats[index] = {
        ...updatedStats[index],
        [field]: updatedValue,
      };
      return { ...prev, playerStats: updatedStats };
    });
  };

  /**
   * Toggle Accordion Sections
   */
  const toggleMatchDetails = () => {
    setIsMatchDetailsOpen((prev) => !prev);
  };

  const togglePlayerStats = () => {
    setIsPlayerStatsOpen((prev) => !prev);
  };

  /**
   * Toggle Individual Player Accordion
   */
  const togglePlayerAccordion = (playerId) => {
    setExpandedPlayers((prev) => ({
      ...prev,
      [playerId]: !prev[playerId],
    }));
  };

  /**
   * Save the New Match
   */
  const handleSave = async () => {
    setLoading(true);
    setError("");

    // Validation: Ensure required fields are filled
    if (!matchData.opposition.trim()) {
      setError("opposition name is required.");
      setLoading(false);
      return;
    }
    if (!matchData.match_date) {
      setError("Match date is required.");
      setLoading(false);
      return;
    }
    if (!selectedTeamId) {
      setError("Please select a team.");
      setLoading(false);
      return;
    }

    // Construct the match object to send
    const requestBody = {
      operationType: "create_match",
      data: {
        team_id: selectedTeamId,
        myTeam: selectedTeamName, // Use the actual team name fetched from API
        opposition: matchData.opposition,
        homeAway: matchData.homeAway,
        match_date: matchData.match_date,
        myTeamScore: matchData.teamScores.myTeamScore,
        oppositionScore: matchData.teamScores.oppositionScore,
        playerStats: matchData.playerStats
          .filter((p) => p.played === "played") // Include only players who played
          .map((p) => ({
            player_id: p.player_id,
            player_name: p.player_name,
            position: p.position,
            played: p.played, // Ensure 'played' field is included
            goals: p.goals,
            assists: p.assists,
            shots_on_target: p.shots_on_target,
            shots_off_target: p.shots_off_target,
            defensive_actions: p.defensive_actions,
            saves: p.saves,
          })),
      },
    };

    // Add a console log to verify the request body
    console.log("Request Body:", JSON.stringify(requestBody, null, 2));

    try {
      const response = await axios.post(API_URL, requestBody, {
        headers: { "Content-Type": "application/json" },
      });
      console.log("[DEBUG] Response from create_match:", response);

      if (response.status === 200) {
        onClose();
        refreshMatches(); // Refresh the main match list
        debugLog("Match created successfully and modal closed.");
      } else {
        setError("Failed to add match. " + (response.data.error || ""));
      }
    } catch (err) {
      console.error("Error saving match:", err);
      setError("Unable to save match. Check console.");
    } finally {
      setLoading(false);
    }
  };

  /**
   * Debugging Log Function
   */
  const debugLog = (message, ...args) => {
    if (DEBUG_MODE) {
      if (args.length > 0) {
        console.log(`[DEBUG] ${message}`, ...args);
      } else {
        console.log(`[DEBUG] ${message}`);
      }
    }
  };

  if (!isOpen) return null;

  return (
    <div className="gs-add-match-modal-overlay">
      <div className="gs-edit-match-modal-content">
        {/* Close Button */}
        <span
          className="gs-add-match-modal-close-button"
          onClick={onClose}
          aria-label="Close Modal"
        >
          &times;
        </span>

        <h2 className="gs-add-match-modal-title">Add New Match</h2>
        {error && (
          <div
            className="gs-edit-match-modal-error-message"
            role="alert"
            aria-live="assertive"
          >
            {error}
          </div>
        )}
        {loading && (
          <div className="gs-add-match-modal-loading">Loading...</div>
        )}

        {/* Team Selection */}
        {teams.length > 1 && (
          <div className="gs-add-match-modal-form-group">
            <label htmlFor="teamSelect">Select Team:</label>
            <select
              id="teamSelect"
              value={selectedTeamId}
              onChange={handleTeamSelection}
              required
              className="gs-add-match-modal-select"
            >
              <option value="">-- Select a Team --</option>
              {teams.map((team) => (
                <option key={team.team_id} value={team.team_id}>
                  {team.team_name}
                </option>
              ))}
            </select>
          </div>
        )}

        {/* Match Details Accordion */}
        <div className="gs-add-match-modal-accordion">
          <div
            className={`gs-add-match-modal-accordion-header ${
              isMatchDetailsOpen ? "active" : ""
            }`}
            onClick={toggleMatchDetails}
            aria-expanded={isMatchDetailsOpen}
            aria-controls="matchDetailsContent"
            role="button"
            tabIndex="0"
            onKeyPress={(e) => {
              if (e.key === "Enter" || e.key === " ") {
                toggleMatchDetails();
              }
            }}
          >
            <span>Match Details</span>
            <span className="gs-add-match-modal-accordion-icon">
              {isMatchDetailsOpen ? "-" : "+"}
            </span>
          </div>
          <div
            id="matchDetailsContent"
            className={`gs-add-match-modal-accordion-content ${
              isMatchDetailsOpen ? "active" : ""
            }`}
          >
            {/* MATCH-LEVEL FIELDS */}
            <div className="gs-add-match-modal-form-group">
              <label htmlFor="oppositionName">Opposition Name:</label>
              <input
                id="oppositionName"
                type="text"
                value={matchData.opposition}
                onChange={(e) =>
                  handleMatchFieldChange("opposition", e.target.value)
                }
                placeholder="Enter opposition team name"
                required
                className="gs-add-match-modal-input"
              />
            </div>
            <div className="gs-add-match-modal-form-group">
              <label htmlFor="homeAwaySelect">Home/Away:</label>
              <select
                id="homeAwaySelect"
                value={matchData.homeAway}
                onChange={(e) =>
                  handleMatchFieldChange("homeAway", e.target.value)
                }
                className="gs-add-match-modal-select"
              >
                <option value="Home">Home</option>
                <option value="Away">Away</option>
              </select>
            </div>
            <div className="gs-add-match-modal-form-group">
              <label htmlFor="matchDate">Match Date:</label>
              <input
                id="matchDate"
                type="datetime-local"
                value={matchData.match_date}
                onChange={(e) =>
                  handleMatchFieldChange("match_date", e.target.value)
                }
                required
                className="gs-add-match-modal-input"
              />
            </div>
            <div className="gs-add-match-modal-form-group gs-add-match-modal-score-group">
              <label htmlFor="myTeamScore">
                {selectedTeamName || "My Team"} Score:
              </label>
              <input
                id="myTeamScore"
                type="number"
                min="0"
                value={matchData.teamScores.myTeamScore}
                onChange={(e) =>
                  handleTeamScoreChange("myTeamScore", e.target.value)
                }
                className="gs-add-match-modal-input gs-add-match-modal-score-input"
              />
              <label
                htmlFor="oppositionScore"
                className="gs-add-match-modal-score-label"
              >
                Opposition Score:
              </label>
              <input
                id="oppositionScore"
                type="number"
                min="0"
                value={matchData.teamScores.oppositionScore}
                onChange={(e) =>
                  handleTeamScoreChange("oppositionScore", e.target.value)
                }
                className="gs-add-match-modal-input gs-add-match-modal-score-input"
              />
            </div>
          </div>
        </div>

        {/* Player Stats Accordion */}
        <div className="gs-add-match-modal-accordion">
          <div
            className={`gs-add-match-modal-accordion-header ${
              isPlayerStatsOpen ? "active" : ""
            }`}
            onClick={togglePlayerStats}
            aria-expanded={isPlayerStatsOpen}
            aria-controls="playerStatsContent"
            role="button"
            tabIndex="0"
            onKeyPress={(e) => {
              if (e.key === "Enter" || e.key === " ") {
                togglePlayerStats();
              }
            }}
          >
            <span>Player Stats</span>
            <span className="gs-add-match-modal-accordion-icon">
              {isPlayerStatsOpen ? "-" : "+"}
            </span>
          </div>
          <div
            id="playerStatsContent"
            className={`gs-add-match-modal-accordion-content ${
              isPlayerStatsOpen ? "active" : ""
            }`}
          >
            {/* PLAYER STATS SECTION */}
            {players.length === 0 && (
              <p className="gs-add-match-modal-no-players">
                {loading
                  ? "Loading players..."
                  : "No players found for the team."}
              </p>
            )}
            {players.map((player, index) => (
              <div
                key={player.player_id} // Unique key using player_id
                className="gs-add-match-modal-player-stat"
              >
                {/* Player Name Header */}
                <div
                  className="gs-add-match-modal-player-name-header"
                  onClick={() => togglePlayerAccordion(player.player_id)}
                  aria-expanded={!!expandedPlayers[player.player_id]}
                  aria-controls={`playerDetails-${player.player_id}`}
                  role="button"
                  tabIndex="0"
                  onKeyPress={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      togglePlayerAccordion(player.player_id);
                    }
                  }}
                >
                  <span>{player.player_name}</span>
                  <span className="gs-add-match-modal-player-accordion-icon">
                    {expandedPlayers[player.player_id] ? "-" : "+"}
                  </span>
                </div>

                {/* Player Details Accordion */}
                {expandedPlayers[player.player_id] && (
                  <div
                    id={`playerDetails-${player.player_id}`}
                    className="gs-add-match-modal-player-details active" // Added 'active' class
                  >
                    <div className="gs-add-match-modal-form-group">
                      <label htmlFor={`position-${player.player_id}`}>
                        Position:
                      </label>
                      <input
                        id={`position-${player.player_id}`}
                        type="text"
                        value={
                          matchData.playerStats[index]
                            ? matchData.playerStats[index].position
                            : ""
                        }
                        onChange={(e) =>
                          handlePlayerStatChange(
                            index,
                            "position",
                            e.target.value
                          )
                        }
                        placeholder="e.g., Forward"
                        className="gs-add-match-modal-input"
                      />
                    </div>
                    <div className="gs-add-match-modal-form-group">
                      <label htmlFor={`played-${player.player_id}`}>
                        Played/Sub:
                      </label>
                      <select
                        id={`played-${player.player_id}`}
                        value={
                          matchData.playerStats[index]
                            ? matchData.playerStats[index].played
                            : "played"
                        }
                        onChange={(e) =>
                          handlePlayerStatChange(
                            index,
                            "played",
                            e.target.value
                          )
                        }
                        className="gs-add-match-modal-select"
                      >
                        <option value="played">Played</option>
                        <option value="sub">Sub</option>
                      </select>
                    </div>
                    <div className="gs-add-match-modal-form-group gs-add-match-modal-stat-group">
                      <label htmlFor={`goals-${player.player_id}`}>
                        Goals:
                      </label>
                      <input
                        id={`goals-${player.player_id}`}
                        type="number"
                        min="0"
                        value={
                          matchData.playerStats[index]
                            ? matchData.playerStats[index].goals
                            : 0
                        }
                        onChange={(e) =>
                          handlePlayerStatChange(index, "goals", e.target.value)
                        }
                        className="gs-add-match-modal-input gs-add-match-modal-stat-input"
                      />
                      <label
                        htmlFor={`assists-${player.player_id}`}
                        className="gs-add-match-modal-stat-label"
                      >
                        Assists:
                      </label>
                      <input
                        id={`assists-${player.player_id}`}
                        type="number"
                        min="0"
                        value={
                          matchData.playerStats[index]
                            ? matchData.playerStats[index].assists
                            : 0
                        }
                        onChange={(e) =>
                          handlePlayerStatChange(
                            index,
                            "assists",
                            e.target.value
                          )
                        }
                        className="gs-add-match-modal-input gs-add-match-modal-stat-input"
                      />
                    </div>
                    <div className="gs-add-match-modal-form-group gs-add-match-modal-stat-group">
                      <label htmlFor={`shots_on_target-${player.player_id}`}>
                        Shots On:
                      </label>
                      <input
                        id={`shots_on_target-${player.player_id}`}
                        type="number"
                        min="0"
                        value={
                          matchData.playerStats[index]
                            ? matchData.playerStats[index].shots_on_target
                            : 0
                        }
                        onChange={(e) =>
                          handlePlayerStatChange(
                            index,
                            "shots_on_target",
                            e.target.value
                          )
                        }
                        className="gs-add-match-modal-input gs-add-match-modal-stat-input"
                      />
                      <label
                        htmlFor={`shots_off_target-${player.player_id}`}
                        className="gs-add-match-modal-stat-label"
                      >
                        Shots Off:
                      </label>
                      <input
                        id={`shots_off_target-${player.player_id}`}
                        type="number"
                        min="0"
                        value={
                          matchData.playerStats[index]
                            ? matchData.playerStats[index].shots_off_target
                            : 0
                        }
                        onChange={(e) =>
                          handlePlayerStatChange(
                            index,
                            "shots_off_target",
                            e.target.value
                          )
                        }
                        className="gs-add-match-modal-input gs-add-match-modal-stat-input"
                      />
                    </div>
                    <div className="gs-add-match-modal-form-group">
                      <label htmlFor={`defensive_actions-${player.player_id}`}>
                        Defensive Actions:
                      </label>
                      <input
                        id={`defensive_actions-${player.player_id}`}
                        type="number"
                        min="0"
                        value={
                          matchData.playerStats[index]
                            ? matchData.playerStats[index].defensive_actions
                            : 0
                        }
                        onChange={(e) =>
                          handlePlayerStatChange(
                            index,
                            "defensive_actions",
                            e.target.value
                          )
                        }
                        className="gs-add-match-modal-input"
                      />
                    </div>
                    <div className="gs-add-match-modal-form-group">
                      <label htmlFor={`saves-${player.player_id}`}>
                        Saves:
                      </label>
                      <input
                        id={`saves-${player.player_id}`}
                        type="number"
                        min="0"
                        value={
                          matchData.playerStats[index]
                            ? matchData.playerStats[index].saves
                            : 0
                        }
                        onChange={(e) =>
                          handlePlayerStatChange(index, "saves", e.target.value)
                        }
                        className="gs-add-match-modal-input"
                      />
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Save / Cancel Buttons */}
        <div className="gs-add-match-modal-buttons">
          <button
            className="gs-save-button gs-btn gs-save-btn"
            onClick={handleSave}
            disabled={loading || (teams.length > 1 && !selectedTeamId)}
            title={
              teams.length > 1 && !selectedTeamId
                ? "Please select a team before saving."
                : "Save Match"
            }
          >
            Save Match
          </button>
          <button
            className="gs-cancel-button gs-btn gs-cancel-btn"
            onClick={onClose}
            disabled={loading}
          >
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
};

export default AddMatchModal;
